/**
 * Multimodal intake helpers — turn a dropped/pasted/picked File (or URL / code)
 * into a MultiAgentAttachment, and resolve it to markdown via the backend
 * /preprocess (Gemini for image/pdf/audio, fetch for URL, passthrough for
 * text/code). Image/pdf/audio resolve on the server; text/code resolve locally.
 */
import {
  preprocessAttachment,
  type MultiAgentAttachment,
  type MultiAgentAttachmentKind,
} from "@/services/multi-agent/api";
import { downscaleImage } from "@/lib/imageResize";

export const MAX_ATTACHMENT_BYTES = 2 * 1024 * 1024; // 2MB
export const MAX_ATTACHMENTS = 6;
export const FILE_ACCEPT =
  "image/*,.pdf,.txt,.md,.markdown,.cls,.trigger,.apex,.js,.ts,.tsx,.jsx,.json,.html,.css,.xml,.yaml,.yml,.py,.java,.sql,.csv";
export const AUDIO_ACCEPT = "audio/*,.mp3,.wav,.m4a,.aac,.aiff,.ogg,.flac,.webm";

// Code file extensions (lowercased) treated as inline code attachments.
const CODE_EXTENSIONS = new Set([
  "cls", "trigger", "apex", "js", "ts", "tsx", "jsx", "json", "html", "css",
  "xml", "yml", "yaml", "py", "java", "sql", "sh", "rb", "go", "rs",
  "c", "cpp", "cs",
]);

function uid(): string {
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

/** Lowercased file extension after the last dot, or undefined when there's no
 *  real extension (no dot, or the dot is at position 0 — i.e. no basename). */
function extFromName(name: string): string | undefined {
  const dot = name.lastIndexOf(".");
  if (dot <= 0 || dot === name.length - 1) return undefined;
  return name.slice(dot + 1).toLowerCase();
}

function langFromName(name: string): string | undefined {
  const ext = extFromName(name);
  if (!ext) return undefined;
  const map: Record<string, string> = {
    cls: "apex", trigger: "apex", apex: "apex", ts: "typescript", tsx: "typescript",
    js: "javascript", jsx: "javascript", py: "python", java: "java", sql: "sql",
    html: "html", css: "css", json: "json", xml: "xml", yml: "yaml", yaml: "yaml",
  };
  return map[ext] || ext;
}

function readBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => {
      const s = String(r.result);
      resolve(s.slice(s.indexOf(",") + 1));
    };
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

const AUDIO_EXTENSIONS = new Set([
  "mp3", "wav", "m4a", "aac", "aiff", "ogg", "flac", "webm",
]);

export function kindForFile(file: File): MultiAgentAttachmentKind {
  const t = file.type || "";
  const ext = extFromName(file.name);
  if (t.startsWith("image/")) return "image";
  if (t === "application/pdf" || ext === "pdf") return "pdf";
  if (t.startsWith("audio/") || (ext !== undefined && AUDIO_EXTENSIONS.has(ext))) return "audio";
  if (ext !== undefined && CODE_EXTENSIONS.has(ext)) return "code";
  return "text";
}

/** File → unresolved attachment (reads bytes/text + image thumbnail). */
export async function fileToAttachment(original: File): Promise<MultiAgentAttachment | { error: string }> {
  // Images: canvas-downscale BEFORE the 2MB gate so big retina/4K screenshot
  // pastes stop being silently rejected and the Gemini payload shrinks.
  let file = original;
  if ((original.type || "").startsWith("image/") && original.type !== "image/svg+xml") {
    try { file = await downscaleImage(original); } catch { /* keep original */ }
  }
  if (file.size > MAX_ATTACHMENT_BYTES) return { error: `${file.name} is over 2MB` };
  const kind = kindForFile(file);
  const att: MultiAgentAttachment = { id: uid(), kind, name: file.name, status: "processing" };
  if (kind === "image") {
    att.mime = file.type || "image/png";
    att.data_base64 = await readBase64(file);
    att.thumbnailDataUrl = `data:${att.mime};base64,${att.data_base64}`;
  } else if (kind === "pdf" || kind === "audio") {
    att.mime = file.type || (kind === "pdf" ? "application/pdf" : "audio/mpeg");
    att.data_base64 = await readBase64(file);
  } else if (kind === "code") {
    att.language = langFromName(file.name);
    att.content = await file.text();
    att.markdown = "```" + (att.language || "") + "\n" + att.content + "\n```";
    att.status = "ready";
  } else {
    att.content = await file.text();
    att.markdown = att.content;
    att.status = "ready";
  }
  return att;
}

export function urlAttachment(url: string): MultiAgentAttachment {
  return { id: uid(), kind: "url", name: url, content: url, status: "processing" };
}

/** A recorded WAV blob (from the mic) → audio attachment for transcription. */
export async function audioBlobToAttachment(
  blob: Blob,
  name = "voice note.wav",
): Promise<MultiAgentAttachment | { error: string }> {
  if (blob.size > MAX_ATTACHMENT_BYTES) return { error: "Recording too long (over 2MB)" };
  const b64 = await new Promise<string>((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => { const s = String(r.result); resolve(s.slice(s.indexOf(",") + 1)); };
    r.onerror = reject;
    r.readAsDataURL(blob);
  });
  return { id: uid(), kind: "audio", name, mime: "audio/wav", data_base64: b64, status: "processing" };
}

/**
 * The language of a pasted block, or `null` when this cannot tell.
 *
 * ── WHY GUESSING IS BOUNDED HERE ────────────────────────────────────────────────────────────────
 *
 * A paste arrives with no filename and no fence tag, so the language is genuinely unknown. It is
 * still worth asking, because the answer is load-bearing in three places: the composer chip can say
 * what the reader pasted, the fence handed to the backend carries the tag (so `ChatCodeBlock`
 * highlights it in the transcript instead of rendering a grey wall), and `language` rides to the
 * run on the attachment itself.
 *
 * `null` IS A REAL ANSWER and the default. §1's no-fabrication rule applies to a language label as
 * much as to a number: naming a file "TypeScript" because it contains a brace would put a wrong
 * fact under the reader's own paste, and a wrong grammar highlights worse than none (the same
 * reasoning `ChatCodeBlock.highlightLanguage` already applies to an unknown tag). So every rule
 * below needs a signal that is close to unique to its language, and anything else returns `null`
 * and renders as plain "Code".
 *
 * Plain string scanning, no regex — this module's convention (see `looksLikeCode`).
 */
export function detectCodeLanguage(text: string): string | null {
  const body = text.slice(0, 4000);
  const lines = body.split("\n").map((l) => l.trim()).filter((l) => l.length > 0);
  if (!lines.length) return null;
  const has = (needle: string) => body.includes(needle);
  const startsAny = (...prefixes: string[]) => lines.some((l) => prefixes.some((p) => l.startsWith(p)));

  // Markup and data first: their opening tokens are unambiguous, and checking them before the
  // brace languages stops a JSON object from being read as JavaScript.
  const first = lines[0];
  if (first.startsWith("<?xml") || (first.startsWith("<") && has("</"))) return "xml";
  if ((first.startsWith("{") || first.startsWith("[")) && has('":')) return "json";

  // Apex BEFORE Java/TS: it shares their keywords, so it is identified by what only it has —
  // SOQL in brackets, its own annotations, its trigger form, `System.debug`.
  if (has("[SELECT ") || has("[Select ") || has("@isTest") || has("@IsTest")) return "apex";
  if (has("System.debug") || startsAny("trigger ")) return "apex";
  if (has(" extends Database.") || has("implements Database.")) return "apex";

  if (startsAny("def ", "async def ") && (has(":\n") || has("self"))) return "python";
  if (startsAny("package ") && has("public class ")) return "java";
  // TypeScript's own syntax, not merely "JavaScript with types" — a bare `const` is in both.
  if (has("interface ") || has(": string") || has(": number") || has(": boolean") || has("as const"))
    return "typescript";
  if (startsAny("import ", "export ") || has("=>") || has("function ")) return "javascript";
  if (startsAny("SELECT ", "select ") && (has(" FROM ") || has(" from "))) return "sql";
  return null;
}

/**
 * A big paste, as an attachment the composer can describe, open and edit.
 *
 * `language` is DETECTED rather than left empty, and the fence is tagged with it when known — the
 * untagged `\`\`\`` this used to emit reached the backend as an unlabelled block and came back
 * through the transcript with no highlighting at all. An undetected language keeps the bare fence,
 * which is exactly what it was before.
 *
 * `name` is the reader's own words for what they did ("Pasted TypeScript"), not a file name: there
 * is no file here, and inventing `snippet.ts` would name something that does not exist (the same
 * rule `codePreview.blockLabel` follows).
 */
export function pastedCodeAttachment(text: string): MultiAgentAttachment {
  const language = detectCodeLanguage(text) ?? "";
  const fence = language ? "```" + language + "\n" : "```\n";
  return {
    id: uid(),
    kind: "code",
    name: language ? `Pasted ${LANGUAGE_LABEL[language] ?? language}` : "Pasted code",
    content: text,
    language,
    markdown: fence + text + "\n```",
    status: "ready",
  };
}

/** Display names for the languages `detectCodeLanguage` can return. */
export const LANGUAGE_LABEL: Readonly<Record<string, string>> = {
  apex: "Apex",
  java: "Java",
  javascript: "JavaScript",
  typescript: "TypeScript",
  python: "Python",
  json: "JSON",
  xml: "XML",
  sql: "SQL",
};

/** `312 lines` / `1 line` — the size of a paste, counted the way a reader would. */
export function lineCountLabel(text: string): string {
  const n = text.trimEnd().split("\n").length;
  return `${n} line${n === 1 ? "" : "s"}`;
}

/**
 * The first line worth showing in a one-line preview.
 *
 * Skips leading blank lines and a lone opening brace — neither says anything about what was pasted,
 * and the chip has room for exactly one line.
 */
export function previewLine(text: string): string {
  for (const raw of text.split("\n")) {
    const line = raw.trim();
    if (!line || line === "{" || line === "[") continue;
    return line.length > 120 ? `${line.slice(0, 120)}…` : line;
  }
  return "";
}

/**
 * Does a SHORT paste (≤1500 chars — a longer one already becomes an attachment via
 * `pastedCodeAttachment`, regardless of this check) look like source code rather than prose?
 *
 * REPORTED: pasting a few lines of TypeScript/Apex from an editor into the composer and sending it
 * produced an unreadable "User Question" — the paste stayed literal, untouched, plain text (the
 * composer never wrapped it in a fence, and nothing downstream detects code that isn't already
 * fenced). This is what decides whether `useAttachments.onComposerPaste` wraps such a paste in a
 * fenced block before it reaches the composer's text, so the SAME markdown renderer the assistant's
 * answers already use (`SkillResultMarkdown`, reused by `UserBubble` — see `TurnView.tsx`) can turn
 * it into a real code block: monospace, preserved indentation, a scrollable box on a long line.
 *
 * DELIBERATELY CONSERVATIVE, and in a specific direction: a false NEGATIVE just leaves a code paste
 * as plain text, which is the pre-existing behaviour and not a new regression. A false POSITIVE
 * fences a paragraph of prose, which is the one outcome explicitly ruled out — "do not simply make
 * the entire User Question a code block… normal text and pasted code should be represented
 * appropriately based on their content." So this asks for a clear MAJORITY of lines to carry
 * code-specific punctuation, not one hit anywhere, and a single-line paste is never wrapped: a
 * one-line paste already reads fine as plain text, and the inline-code case (a reader typing their
 * own `` `backticked` `` word) is a markdown-syntax question the renderer already answers on its
 * own once `UserBubble` renders through it.
 *
 * Plain string scanning, no regex — matches this module's other heuristics
 * (`friendlyAttachmentError` above; `stripLeadingCodeFence` in `services/multi-agent/transcript.ts`
 * for the OTHER half of this same bug report).
 */
export function looksLikeCode(text: string): boolean {
  const lines = text.split("\n").filter((l) => l.trim().length > 0);
  if (lines.length < 2) return false;

  const KEYWORD_STARTS = [
    "// ", "//\t", "# ", "#\t", "/* ", "* ", "import ", "export ", "function ", "async function ",
    "const ", "let ", "var ", "if ", "if(", "else", "return ", "return;", "for ", "for(", "while ",
    "while(", "class ", "public ", "private ", "protected ", "static ", "def ", "package ",
    "using ", "namespace ",
  ];

  let codeLike = 0;
  for (const line of lines) {
    const trimmed = line.trim();
    const last = trimmed[trimmed.length - 1];
    const endsCodey = last === "{" || last === "}" || last === ";" || last === "(" || last === ",";
    const startsCodey =
      trimmed.startsWith("}") ||
      trimmed.startsWith(")") ||
      KEYWORD_STARTS.some((k) => trimmed.startsWith(k));
    const hasArrow = trimmed.includes("=>");
    // An assignment or a call — `foo = bar(` / `foo = [` — rather than a sentence that merely
    // contains an equals sign ("x = y" as prose is rare enough that this pairing is the guard).
    const hasAssignOrCall =
      (trimmed.includes(" = ") && (trimmed.includes("(") || trimmed.includes("["))) ||
      (trimmed.includes("(") && trimmed.includes(")") && trimmed.includes(";"));
    // Indentation beyond column 0 is common to nearly every language's block structure and rare in
    // pasted prose (which wraps, it does not indent).
    const indented = line.length > trimmed.length && (line[0] === " " || line[0] === "\t");
    if (endsCodey || startsCodey || hasArrow || hasAssignOrCall || indented) codeLike++;
  }
  return codeLike / lines.length >= 0.6;
}

/** Map a raw backend fetch/extract error to plain user guidance. Plain string
 *  matching only (no regex per project rule). Falls back to the raw reason. */
export function friendlyAttachmentError(raw: string | null | undefined, kind: MultiAgentAttachmentKind): string {
  const reason = (raw || "").trim();
  const low = reason.toLowerCase();
  if (low.includes("non-public") || low.includes("private") || low.includes("loopback") || low.includes("not allowed for this address"))
    return "That address is private/internal — only public web pages can be fetched.";
  if (low.includes("content-type") || low.includes("content type") || low.includes("not allowed"))
    return "That page type can't be read (only HTML / plain text is supported).";
  if (low.includes("timeout") || low.includes("timed out"))
    return "The page took too long to load — try again or paste the text directly.";
  if (low.includes("no readable text") || low.includes("empty"))
    return kind === "url" ? "That page had no readable text to extract." : "Nothing readable could be extracted.";
  if (low.includes("could not be fetched") || low.includes("dns") || low.includes("connection") || low.includes("refused"))
    return "Couldn't reach that page — check the URL and that it's publicly accessible.";
  return reason || "could not process this attachment";
}

/** Resolve a server-side kind (image/pdf/audio/url) → markdown. text/code are
 *  already resolved locally. Returns the updated attachment. */
export async function resolveAttachment(att: MultiAgentAttachment): Promise<MultiAgentAttachment> {
  if (att.markdown && att.status === "ready") return att; // already resolved (text/code)
  try {
    const r = await preprocessAttachment({
      kind: att.kind, name: att.name, mime: att.mime,
      language: att.language, content: att.content, data_base64: att.data_base64,
    });
    const ok = r.ok !== false && !!(r.markdown && r.markdown.trim());
    // Truncation signal (image/pdf/audio): explicit flag OR a non-STOP finish.
    const truncated =
      r.truncated === true ||
      (typeof r.finish_reason === "string" &&
        r.finish_reason.length > 0 &&
        r.finish_reason.toUpperCase() !== "STOP");
    return {
      ...att,
      markdown: r.markdown || "",
      status: ok ? "ready" : "error",
      errorReason: ok ? undefined : friendlyAttachmentError(r.error, att.kind),
      // URL chip metadata (optional — undefined when the backend doesn't send it).
      title: r.title || att.title,
      favicon: r.favicon || att.favicon,
      truncated: truncated || undefined,
    };
  } catch (e) {
    return {
      ...att,
      status: "error",
      errorReason: friendlyAttachmentError((e as Error)?.message, att.kind),
    };
  }
}
